import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ChevronRight, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ProductionDeclarationMaterialStatusBadge } from '@/features/production-declaration/components/statement/production-declaration-material-status-badge';
import type { ProductionDeclarationMaterial } from '@/types/core/purchase-order/production-declaration-material';
import { ProductionDeclarationMaterialStatusCode } from '@/types/core/purchase-order/production-declaration-material-status-code';

const MATERIALS_SKELETON_ROW_KEYS = [
  'material-skeleton-1',
  'material-skeleton-2',
  'material-skeleton-3',
  'material-skeleton-4',
] as const;

type ProductionDeclarationMaterialsListProps = {
  materials: ProductionDeclarationMaterial[];
  isLoading?: boolean;
};

export function ProductionDeclarationMaterialsList({
  materials,
  isLoading = false,
}: ProductionDeclarationMaterialsListProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { id } = useParams({ strict: false });
  const { token } = useSearch({ strict: false });
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const productionDeclarationId = `${id}`;
  const statusAudience = isAuthenticated ? 'internal' : 'supplier';
  const hasMaterialToCorrect = materials.some(
    (material) =>
      material.status.code === ProductionDeclarationMaterialStatusCode.Rejected,
  );
  const navigateToMaterial = (selectedMaterialId: string) =>
    navigate({
      params: {
        id: productionDeclarationId,
        materialId: selectedMaterialId,
      },
      search: { token },
      to: '/production-declarations/$id/materials/$materialId',
    });

  return (
    <section className='rounded-[1.75rem] border border-declaration-border bg-white px-7 py-8 shadow-[0_10px_24px_rgba(17,24,39,0.06)] sm:px-10'>
      <div className='space-y-3'>
        <div className='flex items-center gap-2'>
          <h2 className='font-semibold text-[1.65rem] text-declaration-foreground leading-none tracking-tight'>
            {t('materials.title')}
          </h2>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={t('materials.tooltip.title')}
                className='rounded-sm text-declaration-muted-foreground transition-colors hover:text-declaration-foreground-muted'
                type='button'
              >
                <Info aria-hidden className='size-5' />
              </button>
            </TooltipTrigger>
            <TooltipContent
              align='start'
              className='w-[min(38rem,calc(100vw-2rem))] space-y-3 rounded-xl border border-declaration-border bg-white p-4 text-left text-declaration-foreground text-sm leading-6 shadow-[0_10px_24px_rgba(17,24,39,0.08)] dark:border-declaration-border dark:bg-white dark:text-declaration-foreground'
              collisionPadding={24}
              showArrow={false}
              side='bottom'
              sideOffset={10}
            >
              <p className='font-semibold text-declaration-foreground text-sm'>
                {t('materials.tooltip.title')}
              </p>
              <p>{t('materials.tooltip.intro')}</p>
              <ul className='list-disc space-y-1 pl-5'>
                <li>{t('materials.tooltip.bullet1')}</li>
                <li>{t('materials.tooltip.bullet2')}</li>
              </ul>
              <p className='font-semibold text-declaration-foreground text-sm'>
                {t('materials.tooltip.supplierActionTitle')}
              </p>
              <ul className='list-disc space-y-1 pl-5'>
                <li>{t('materials.tooltip.actionBullet1')}</li>
                <li>{t('materials.tooltip.actionBullet2')}</li>
                <li>{t('materials.tooltip.actionBullet3')}</li>
              </ul>
            </TooltipContent>
          </Tooltip>
        </div>
        <p className='max-w-5xl text-[1.05rem] text-declaration-foreground-muted leading-7'>
          {t('materials.description')}
        </p>
        {statusAudience === 'supplier' && hasMaterialToCorrect ? (
          <p className='max-w-5xl font-semibold text-red-600 text-sm leading-6'>
            {t('materials.correctionNotice')}
          </p>
        ) : null}
      </div>

      <div className='mt-7'>
        {isLoading ? (
          <Table className='table-fixed'>
            <TableHeader>
              <TableRow className='border-declaration-muted hover:bg-transparent'>
                <TableHead className='px-3 pb-3 text-declaration-muted-foreground text-sm uppercase tracking-[0.08em]'>
                  {t('materials.columns.woodType')}
                </TableHead>
                <TableHead className='w-[10rem] px-3 pb-3 text-declaration-muted-foreground text-sm uppercase tracking-[0.08em]'>
                  {t('materials.columns.hsCode')}
                </TableHead>
                <TableHead className='w-[10rem] px-3 pb-3 text-declaration-muted-foreground text-sm uppercase tracking-[0.08em]'>
                  {t('materials.columns.status')}
                </TableHead>
                <TableHead className='w-[3.5rem] px-3 pb-3' />
              </TableRow>
            </TableHeader>
            <TableBody>
              {MATERIALS_SKELETON_ROW_KEYS.map((rowKey) => (
                <TableRow
                  className='border-declaration-border-light hover:bg-transparent'
                  key={rowKey}
                >
                  <TableCell className='px-3 py-5'>
                    <div className='h-8 rounded-full bg-declaration-skeleton' />
                  </TableCell>
                  <TableCell className='px-3 py-5'>
                    <div className='h-8 rounded-full bg-declaration-skeleton' />
                  </TableCell>
                  <TableCell className='px-3 py-5'>
                    <div className='h-8 rounded-full bg-declaration-skeleton' />
                  </TableCell>
                  <TableCell className='px-3 py-5'>
                    <div className='ml-auto h-8 w-8 rounded-md bg-declaration-skeleton' />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : materials.length === 0 ? (
          <div className='py-10 text-center text-declaration-muted-foreground'>
            {t('materials.empty')}
          </div>
        ) : (
          <Table className='table-fixed'>
            <TableHeader>
              <TableRow className='border-declaration-muted hover:bg-transparent'>
                <TableHead className='px-3 pb-3 text-declaration-muted-foreground text-sm uppercase tracking-[0.08em]'>
                  {t('materials.columns.woodType')}
                </TableHead>
                <TableHead className='w-[10rem] px-3 pb-3 text-declaration-muted-foreground text-sm uppercase tracking-[0.08em]'>
                  {t('materials.columns.hsCode')}
                </TableHead>
                <TableHead className='w-[10rem] px-3 pb-3 text-declaration-muted-foreground text-sm uppercase tracking-[0.08em]'>
                  {t('materials.columns.status')}
                </TableHead>
                <TableHead className='w-[3.5rem] px-3 pb-3' />
              </TableRow>
            </TableHeader>
            <TableBody>
              {materials.map((material) => (
                <TableRow
                  className='cursor-pointer border-declaration-border-light transition-colors hover:bg-declaration-hover'
                  key={material.id}
                  onClick={() => navigateToMaterial(material.id)}
                >
                  <TableCell className='px-3 py-5 font-semibold text-[1.05rem] text-declaration-foreground leading-7'>
                    {material.materialEnglishName}
                  </TableCell>
                  <TableCell className='w-[10rem] px-3 py-5 text-[1.05rem] text-declaration-foreground-muted leading-7'>
                    {material.materialHsCode ?? '—'}
                  </TableCell>
                  <TableCell className='w-[10rem] px-3 py-5'>
                    <ProductionDeclarationMaterialStatusBadge
                      audience={statusAudience}
                      code={material.status.code}
                    />
                  </TableCell>
                  <TableCell className='w-[3.5rem] px-3 py-5 text-right'>
                    <div className='flex justify-end'>
                      <button
                        aria-label={t('materials.openMaterial')}
                        className='inline-flex size-9 items-center justify-center rounded-md text-declaration-foreground transition-colors hover:bg-declaration-hover'
                        onClick={(event) => {
                          event.stopPropagation();
                          navigateToMaterial(material.id);
                        }}
                        type='button'
                      >
                        <ChevronRight className='size-6' strokeWidth={2.4} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  );
}
